import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { useJourney, getWeeks, gateProgress, readJourney } from "@/lib/journey";
import { useAuth } from "@/lib/auth";
import { allMastered, celebrateMastery } from "@/lib/certificate";
import { Ring } from "./Ring";
import { Reveal } from "./Reveal";
import Blessing from "./Blessing";
import ReminderOptIn from "./ReminderOptIn";

export default function PracticeJournal({ i }) {
  const { data, toggleWeek, setNote } = useJourney();
  const { user } = useAuth();
  const [blessed, setBlessed] = useState(false);
  const close = useCallback(() => setBlessed(false), []);
  const entry = data[i];
  const weeks = getWeeks(i);
  const done = gateProgress(entry);
  if (!weeks.length) return null;
  const tick = (k) => {
    const finishing = k === weeks.length - 1 && !entry?.weeks?.[k];
    toggleWeek(i, k);
    if (finishing) setBlessed(true);
    if (finishing && user?.initiate && allMastered(readJourney())) celebrateMastery(user.name, readJourney()).catch(() => {});
  };

  return (
    <section data-testid="practice-journal" className="py-20 sm:py-28 border-t border-[#D9B47A]/15">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-8">
        <div>
          <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Your Practice Journal</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
            Three weeks. <em className="text-[#D9B47A]">One</em> devotion.
          </h2>
          <p className="mt-4 text-[#A8998C] max-w-xl">Tick each week as you complete it. Your journal lives privately in this browser only.</p>
        </div>
        <Ring value={done / 3} size={128} testId="journal-gate-ring">
          <div>
            <p className="font-display text-4xl text-[#F2E8DF]">{done}<span className="text-[#8F8176] text-2xl">/3</span></p>
            <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#A8998C]">weeks</p>
          </div>
        </Ring>
      </Reveal>

      <div className="mt-12 grid md:grid-cols-3 gap-4">
        {weeks.map((w, k) => {
          const on = Boolean(entry?.weeks?.[k]);
          return (
            <motion.button
              key={w.title}
              type="button"
              whileTap={{ scale: 0.98 }}
              data-testid={`journal-week-${k + 1}`}
              aria-pressed={on}
              onClick={() => tick(k)}
              className={`group text-left p-7 border transition-colors duration-500 ${on ? "border-[#C2414F] bg-[#5A0F1A]/30" : "border-[#D9B47A]/20 hover:border-[#D9B47A]/60 bg-[#0D0908]"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">Week {k + 1}</span>
                <span className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 ${on ? "bg-[#C2414F] border-[#C2414F]" : "border-[#D9B47A]/40 group-hover:border-[#D9B47A]"}`}>
                  {on && <Check className="w-4 h-4 text-[#F2E8DF]" />}
                </span>
              </div>
              <p className="font-display text-2xl mt-5">{w.name}</p>
              {w.duration && <p className="text-sm text-[#D9B47A] mt-2">{w.duration}</p>}
              <p className="text-[13px] text-[#A8998C] mt-4">
                {on ? `Completed ${new Date(entry.weeks[k]).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : "Tap when this week is complete"}
              </p>
            </motion.button>
          );
        })}
      </div>

      <label className="block mt-10">
        <span className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">Reflection</span>
        <textarea
          data-testid="journal-note"
          maxLength={2000}
          rows={3}
          value={entry?.note ?? ""}
          onChange={(e) => setNote(i, e.target.value)}
          placeholder="What did your body reveal at this gate?"
          className="mt-3 w-full bg-transparent border-b border-[#D9B47A]/30 py-3 text-lg text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] resize-none transition-colors"
        />
      </label>
      <Link to="/journey" data-testid="journal-view-journey" className="mt-8 inline-flex items-center gap-2 font-display italic text-xl text-[#D9B47A] hover:text-[#F2E8DF] transition-colors">
        See your journey through the 13 Gates <ArrowUpRight className="w-4 h-4" />
      </Link>
      <Blessing i={i} open={blessed} onClose={close} />
      <div className="mt-12">
        <ReminderOptIn />
      </div>
    </section>
  );
}
